import { BehaviorSubject, combineLatest, filter, map, of, shareReplay, switchMap, tap } from "rxjs";
import { odataGet, odataList } from "./odataApi";
import { getAuthService } from "./auth.service";
import { hasPolicy$ } from "helpers/hasPolicy";
import { initService } from "helpers/initService";
import { getClientService } from "./client.service";
import { getUserService } from "./user.service";

class ClientsService {
  private readonly _refetchAt$ = new BehaviorSubject<Date>(new Date());
  private readonly _getClientsOberservable = new Map<number, ReturnType<typeof this._getClient>>();
  public readonly clients$ = combineLatest([
    getUserService().user$,
    hasPolicy$("SuperAdmin"),
    this._refetchAt$,
  ]).pipe(
    filter(([user]) => !!user),
    switchMap(([user, isSuper]) => {
      const o = odataList("getClients", {}, { noClientId: true })
        .select("id", "name", "hsCompanyId", "tier")
        .orderBy("name")
        .expandAndSelect("userAndClients", (a) => a.filter("userId", "eq", user!.id), "roleId")
        .expandAndSelect("team", null, "orgId")
        .expandAndSelect("org", null, "enterpriseId")
        .top(600);
      if (isSuper) {
        return getClientService().client$.pipe(
          filter((c) => !!c),
          switchMap((c) =>
            o
              .filterOr(
                ["id", "eq", c.id!],
                ["id", "eq", c.orgId!],
                ["id", "eq", c.enterpriseId!],
                ["extendedTeam.orgId", "eq", c.orgId ?? c.id],
                ["extendedTeam.enterpriseId", "eq", c.enterpriseId ?? c.id],
              )
              .$(),
          ),
        );
      }
      return o.$().pipe(tap((c) => c.value && !c.value.length && getAuthService().logout()));
    }),
    map((c) => c.value),
    shareReplay(1),
  );

  public searchClients = (search: string) => {
    if (search.length < 1) {
      return of([]);
    }
    return odataList("getClients", {}, { noClientId: true })
      .select("id", "name", "nameClean", "hsCompanyId", "tier")
      .filterSearchOr(["name", "nameClean"], [search])
      .orderBy("name")
      .$()
      .pipe(map((c) => c.value));
  };

  public getClient = (id: number | undefined) => {
    if (id === undefined) {
      return of(null);
    }
    if (!this._getClientsOberservable.has(id)) {
      this._getClientsOberservable.set(id, this._getClient(id));
    }
    return this._getClientsOberservable.get(id)!;
  };
  private readonly _getClient = (id: number) => {
    return odataGet("getClientsOne", { key: id }, { noClientId: true })
      .select("id", "name", "hsCompanyId", "tier")
      .$()
      .pipe(shareReplay(1));
  };
  constructor() {}

  public refetch = () => this._refetchAt$.next(new Date());
}

let clientsService: Readonly<ClientsService> | null = null;

export const getClientsService = () => {
  if (!clientsService) {
    clientsService = initService(() => new ClientsService());
  }
  return clientsService;
};
